import { databaseService } from '@/services/databaseService';
import { isAnswerPatch, hasAnswerObservation } from '@/services/checklist/checklistObservations';
import type { ChecklistValueV2LocalRow, ChecklistValueV2Outcome } from '@/types/syncRecovery';
import { pendingMediaMatchesValue } from './ChecklistMediaObservationContract';
import { sha512Hex } from './PendingOperationTerminalization';
import { assertPendingSyncSessionCurrent, createPendingSyncSupabaseClient,
  isPendingSyncSessionSnapshotStateCurrent, type PendingSyncSessionSnapshot } from './PendingSyncSession';

/** Reconcilia a nota enviada antes da captura, sem adotar escolhas ou notas remotas divergentes. */
export async function acknowledgeChecklistMediaObservation(input: {
  checklistId: string; answers: Record<string, unknown>; localValues: readonly ChecklistValueV2LocalRow[];
  outcomes: readonly ChecklistValueV2Outcome[]; snapshot: PendingSyncSessionSnapshot;
}): Promise<void> {
  const { snapshot } = input;
  const client = createPendingSyncSupabaseClient(snapshot);
  for (const outcome of input.outcomes) {
    const answer = input.answers[outcome.fieldId];
    const payload = outcome.payload;
    if (!outcome.terminal || !['server_applied', 'historical_materialized'].includes(outcome.accountingState) ||
        !isAnswerPatch(answer) || !hasAnswerObservation(answer) || Object.prototype.hasOwnProperty.call(answer, 'valor') ||
        outcome.valueId !== payload.id || outcome.idempotencyKey !== payload.idempotency_key ||
        payload.observacao !== answer.observacao) continue;
    const original = input.localValues.find(row => row.id === payload.id);
    if (!original || original.empresa_id !== snapshot.empresaId || original.checklist_id !== input.checklistId ||
        (original.valor ?? null) !== (payload.valor ?? null)) continue;
    await assertPendingSyncSessionCurrent(snapshot);
    const value = await databaseService.readItem<Record<string, any>>('guincho_valores_checklist', payload.id);
    const images = await databaseService.listItems<Record<string, any>>('pending_images', { item_id: input.checklistId });
    if (!value) continue;
    const linked = images.filter(pending => pending.user_id === snapshot.userId && pendingMediaMatchesValue(pending, value));
    if (linked.length !== 1) continue;
    const pending = linked[0];
    const { data: remote, error } = await client.from('guincho_valores_checklist')
      .select('id,empresa_id,checklist_id,campo_id,snapshot_field_id,valor,observacao,updated_at')
      .eq('id', payload.id).eq('empresa_id', snapshot.empresaId).eq('checklist_id', input.checklistId).maybeSingle();
    await assertPendingSyncSessionCurrent(snapshot);
    if (error) throw error;
    if (!remote || remote.id !== payload.id || remote.empresa_id !== snapshot.empresaId ||
        remote.checklist_id !== input.checklistId || (remote.valor ?? null) !== (payload.valor ?? null) ||
        (remote.observacao ?? null) !== payload.observacao || typeof remote.updated_at !== 'string' ||
        !Number.isFinite(Date.parse(remote.updated_at))) continue;
    if ((remote.snapshot_field_id ?? null) !== (payload.snapshot_field_id ?? null) ||
        (remote.campo_id ?? null) !== (payload.campo_id ?? null)) {
      const snapshotFieldId = payload.snapshot_field_id || remote.snapshot_field_id;
      if (!snapshotFieldId || (payload.snapshot_field_id && remote.snapshot_field_id !== payload.snapshot_field_id)) continue;
      const field = await client.from('guincho_checklist_snapshot_fields').select('id,empresa_id,snapshot_id,original_campo_id')
        .eq('id', snapshotFieldId).eq('empresa_id', snapshot.empresaId).maybeSingle();
      const parent = await client.from('guincho_checklists').select('id,empresa_id,snapshot_id,retired_at')
        .eq('id', input.checklistId).eq('empresa_id', snapshot.empresaId).maybeSingle();
      await assertPendingSyncSessionCurrent(snapshot);
      if (field.error || parent.error || !field.data || !parent.data ||
          field.data.id !== snapshotFieldId || field.data.empresa_id !== snapshot.empresaId ||
          parent.data.id !== input.checklistId || parent.data.empresa_id !== snapshot.empresaId || parent.data.retired_at ||
          parent.data.snapshot_id !== field.data.snapshot_id || !field.data.original_campo_id ||
          (payload.campo_id && payload.campo_id !== field.data.original_campo_id) ||
          (remote.campo_id && remote.campo_id !== field.data.original_campo_id)) continue;
    }
    await databaseService.advanceChecklistMediaObservationBaseline({ pending, value,
      previousRemoteValue: payload.valor, previousRemoteUpdatedAt: payload.expected_updated_at,
      remoteUpdatedAt: remote.updated_at,
      canAcknowledge: () => isPendingSyncSessionSnapshotStateCurrent(snapshot),
    });
  }
}

/** Reutiliza a evidência terminal já persistida quando a rede falhou depois da RPC. */
export async function retryAcknowledgedChecklistMediaObservations(snapshot: PendingSyncSessionSnapshot, checklistId?: string): Promise<void> {
  const images = await databaseService.listItems<Record<string, any>>('pending_images');
  const checklistIds = new Set(images.filter(row => row.item_type === 'CHECKLIST' && row.user_id === snapshot.userId &&
    row.empresa_id === snapshot.empresaId && (!checklistId || row.item_id === checklistId)).map(row => row.item_id as string));
  if (!checklistIds.size) return;
  await assertPendingSyncSessionCurrent(snapshot);
  for (const id of checklistIds) {
    const archives = await databaseService.listItems<Record<string, any>>('accounted_pending_operations', { item_id: id });
    for (const archive of archives) {
      if (archive.empresa_id !== snapshot.empresaId || archive.user_id !== snapshot.userId ||
          archive.operation !== 'checklist_partial_update' || archive.collection !== 'guincho_valores_checklist' ||
          !['server_applied', 'historical_materialized'].includes(archive.accounting_state)) continue;
      let data;
      try { data = JSON.parse(archive.data); } catch { continue; }
      const payload = data?.payload;
      if (data?.media_observation_ack?.version !== 1 || !data.media_observation_ack.original_value ||
          !payload || !data.field_id || data.idempotency_key !== payload.idempotency_key ||
          sha512Hex(JSON.stringify({ id: payload.id, campo_id: payload.campo_id,
            snapshot_field_id: payload.snapshot_field_id, valor: payload.valor,
            ...(payload.observacao !== undefined ? { observacao: payload.observacao } : {}),
            expected_updated_at: payload.expected_updated_at,
            ...(payload.expected_partner_revision !== undefined ? { expected_partner_revision: payload.expected_partner_revision } : {}),
            idempotency_key: payload.idempotency_key })) !== archive.payload_sha512) continue;
      await acknowledgeChecklistMediaObservation({ checklistId: id,
        answers: { [data.field_id]: { observacao: payload.observacao } },
        localValues: [data.media_observation_ack.original_value], snapshot,
        outcomes: [{ fieldId: data.field_id, valueId: payload.id, idempotencyKey: payload.idempotency_key,
          payload, payloadSha512: archive.payload_sha512, accountingState: archive.accounting_state,
          reason: archive.reason, terminal: true }],
      });
    }
  }
}
